import { trigger, transition, style, animate } from '@angular/animations';
import { NgClass } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { DropdownOption, ButtonVariant } from '@shared-models';
import { Note } from '@shared-models';

/**
 * Dropdown button component with multiple variants and selection modes
 * Implements ControlValueAccessor for reactive forms integration
 *
 * @example
 * // Single selection
 * <v-one-dropdown-button
 *   [formControl]="myControl"
 *   [options]="options"
 *   [variant]="'primary'">
 * </v-one-dropdown-button>
 *
 * @example
 * // Multiple selection with checkboxes
 * <v-one-dropdown-button
 *   [formControl]="myMultiControl"
 *   [options]="options"
 *   [multiple]="true"
 *   [variant]="'outline'">
 * </v-one-dropdown-button>
 */
@Component({
  selector: 'v-one-dropdown-button',
  imports: [NgClass, forwardRef(() => VOneIconComponent), TranslatePipe],
  templateUrl: './v-one-dropdown-button.component.html',
  styleUrls: ['./v-one-dropdown-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VOneDropdownButtonComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'translateY(-10px)' }),
        ),
      ]),
    ]),
  ],
})
export class VOneDropdownButtonComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  readonly ICON_TYPE = IconType;

  notes = input<Note[]>([]);
  @Input() variant: ButtonVariant = 'primary';
  @Input() options: DropdownOption[] = [];
  placeholder = input<string>('Select option');
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() withIcon = true;
  @Input() withDot = false;
  @Input() iconPosition: 'left' | 'right' = 'right';
  @Input() closeOnSelect = true;
  @Input() showSelectionCount = true;

  @Input() customIconName = 'chevronDown';
  @Input() iconType = this.ICON_TYPE.OUTLINE;
  @Input() iconRotateOnOpen = true;

  @Output() selectionChange = new EventEmitter<
    DropdownOption | DropdownOption[]
  >();

  isOpen = signal(false);
  focusedIndex = -1;
  selectedOptions = signal<DropdownOption[]>([]);

  readonly iconColor = computed(() => {
    const currentVariant = this.variant;
    const open = this.isOpen();

    switch (currentVariant) {
      case 'text':
        return open ? 'var(--color-blue-400)' : 'var(--color-blue-700)';

      case 'outline':
        return 'var(--color-blue-700)';

      case 'secondary':
        return 'var(--dropdown-secondary-icon-color, var(--color-blue-700))';

      case 'primary':
        return 'var(--color-white)';

      default:
        return 'currentColor';
    }
  });

  creditNotesCount = computed(
    () => this.notes().filter((nota) => nota.tipo_nota === 'Credito').length,
  );

  debitNotesCount = computed(
    () => this.notes().filter((nota) => nota.tipo_nota === 'Debito').length,
  );

  buttonLabel = computed(() => {
    const selected = this.selectedOptions();
    if (selected.length === 0) return this.placeholder();

    if (!this.showSelectionCount) {
      return this.placeholder();
    }

    if (this.multiple) {
      if (selected.length === 1) return selected[0].label;
      return `${selected.length} selected`;
    }

    return selected[0]?.label || this.placeholder();
  });

  private internalValue?: string | string[];
  private onChange: (value: string | string[] | undefined) => void = () => {
    // Placeholder for ControlValueAccessor
  };
  private onTouched: () => void = () => {
    // Placeholder for ControlValueAccessor
  };

  get value(): string | string[] | undefined {
    return this.internalValue;
  }

  writeValue(value: string | string[] | undefined): void {
    this.internalValue = value;
    this.initializeSelection();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | string[] | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  private initializeSelection(): void {
    if (!this.internalValue) {
      this.selectedOptions.set([]);
      return;
    }

    if (this.multiple && Array.isArray(this.internalValue)) {
      const selected = this.options.filter((opt) =>
        (this.internalValue as string[]).includes(opt.value),
      );
      this.selectedOptions.set(selected);
    } else if (typeof this.internalValue === 'string') {
      const selected = this.options.find(
        (opt) => opt.value === this.internalValue,
      );
      if (selected) this.selectedOptions.set([selected]);
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen.update((state) => !state);
      if (!this.isOpen()) {
        this.onTouched();
      }
      this.cdr.markForCheck();
    }
  }

  selectOption(option: DropdownOption): void {
    if (option.disabled) return;

    if (this.multiple) {
      this.toggleMultipleSelection(option);
    } else {
      this.selectSingleOption(option);
    }
  }

  private selectSingleOption(option: DropdownOption): void {
    this.selectedOptions.set([option]);
    this.internalValue = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.selectionChange.emit(option);

    if (this.closeOnSelect) {
      this.isOpen.set(false);
    }
    this.focusedIndex = -1;
    this.cdr.markForCheck();
  }

  private toggleMultipleSelection(option: DropdownOption): void {
    const current = this.selectedOptions();
    const isSelected = current.some((opt) => opt.value === option.value);

    let newSelection: DropdownOption[];
    if (isSelected) {
      newSelection = current.filter((opt) => opt.value !== option.value);
    } else {
      newSelection = [...current, option];
    }

    this.selectedOptions.set(newSelection);
    const values = newSelection.map((opt) => opt.value);
    this.internalValue = values;
    this.onChange(values);
    this.selectionChange.emit(newSelection);
    this.cdr.markForCheck();
  }

  isOptionSelected(option: DropdownOption): boolean {
    return this.selectedOptions().some((opt) => opt.value === option.value);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.focusedIndex = -1;
    this.onTouched();
    this.cdr.markForCheck();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousOption();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.focusedIndex >= 0) {
          const option = this.options[this.focusedIndex];
          if (!option.disabled) {
            this.selectOption(option);
          }
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private focusNextOption(): void {
    let nextIndex = this.focusedIndex + 1;
    while (
      nextIndex < this.options.length &&
      this.options[nextIndex].disabled
    ) {
      nextIndex++;
    }
    if (nextIndex < this.options.length) {
      this.focusedIndex = nextIndex;
      this.cdr.markForCheck();
    }
  }

  private focusPreviousOption(): void {
    let prevIndex = this.focusedIndex - 1;
    while (prevIndex >= 0 && this.options[prevIndex].disabled) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      this.focusedIndex = prevIndex;
      this.cdr.markForCheck();
    }
  }
}
